import { useMutation } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import type {
  CalculateSavedPaymentAccountTransactionsResponse,
  SavedPaymentAccountRefundAmounts,
} from "@/api/cashier";
import { EnumActionType, EnumErrorKey } from "@/api/cashier";
import { calculateSavedPaymentAccountTransactionsMutation } from "@/api/cashier/@tanstack/react-query.gen";
import type { UploadedFile } from "@/components/bank-statement-upload/BankStatementUpload";
import {
  getSuccessfulBankStatementId,
  hasBankStatementError,
  hasBankStatementInFlight,
} from "@/components/bank-statement-upload/bankStatementGate";
import { isBankStatementMandatory } from "@/components/bank-statement-upload/isBankStatementMandatory";
import { uploadBankStatement } from "@/components/bank-statement-upload/uploadBankStatements";
import { ErrorMessage } from "@/components/ErrorMessage";
import { ErrorPage } from "@/components/error-page/ErrorPage";
import { InfoBox } from "@/components/general/info-box/InfoBox";
import { NavButtons } from "@/components/general/nav-buttons/NavButtons";
import { InstructionsModal } from "@/components/modals/instructions-modal/InstructionsModal";
import { notify } from "@/components/toast/notify.ts";
import toastStyles from "@/components/toast/Toast.module.css";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useDynamicForm } from "@/hooks/deposit/useDynamicForm";
import { useLogger } from "@/hooks/useLogger";
import { useUserData } from "@/hooks/useUserData";
import { serializeError } from "@/utils/log";
import { WITHDRAW_INSTRUCTIONS } from "@/mock/Instructions";
import { AccountBalanceCard } from "@/pages/withdrawals/choose-method/account-balance-card/AccountBalanceCard";
import { MethodSelector } from "@/pages/withdrawals/choose-method/withdrawal-methods-selector/MethodSelector";
import type {
  AmountsByMethodId,
  CurrencyConversionBySpaAndCode,
  NavigatableWithdrawalProfitAmounts,
  RefundContext,
  WithdrawalProfitAmounts,
} from "@/pages/withdrawals/types";
import {
  getNewProfitMethodErrorMessage,
  useNewProfitMethod,
} from "@/pages/withdrawals/useNewProfitMethod";
import {
  REFUND_BALANCE_REFETCH_INTERVAL_MS,
  useWithdrawalData,
} from "@/pages/withdrawals/useWithdrawalData";
import {
  FunnelFlow,
  FunnelStep,
  trackFunnelStep,
} from "@/observability/funnel";
import { useAppSelector } from "@/redux/hooks";
import { ERoutes } from "@/utils/enums";
import {
  calcMaxRefunds,
  calculateRefundAmountLeft,
  decimalToAmountCurrencyRequest,
  getRefundAccountsByIdAndCurrency,
  isCardForm,
  majorToAmountCurrencyRequest,
  moneyToDecimal,
} from "@/utils/helpers";
import { isExchangeRateTechnicalIssue } from "@/utils/paymentMethodStatus";
import { addPreselectedAddNewRefundEntries } from "@/utils/refundPreselect";

export const ChooseMethod = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logger = useLogger();
  const { selectedAccount } = useAccount();
  const { data: userData } = useUserData();
  const [profitAmounts, setProfitAmounts] = useState<WithdrawalProfitAmounts>();
  const [bankStatementFile, setBankStatementFile] = useState<
    UploadedFile | undefined
  >(undefined);

  const {
    balance,
    balanceIsLoading,
    balanceDataUpdatedAt,
    profitMethods,
    newProfitMethods,
    methodsIsLoading,
    refundMethodsByLedger,
    refetch,
    profitMethodsRefetch,
    error,
  } = useWithdrawalData(selectedAccount);

  const exchangeRateUnavailable =
    (profitMethods ?? []).some(isExchangeRateTechnicalIssue) ||
    (newProfitMethods?.payment_methods ?? []).some(
      isExchangeRateTechnicalIssue
    ) ||
    (balance?.refund_accounts ?? []).some(isExchangeRateTechnicalIssue) ||
    Object.values(balance?.payout_accounts ?? {}).some(
      (entry) =>
        isExchangeRateTechnicalIssue(entry.payment_method) ||
        (entry.accounts ?? []).some(isExchangeRateTechnicalIssue)
    );

  useEffect(() => {
    if (selectedAccount) {
      setProfitAmounts(undefined);
      setBankStatementFile(undefined);
    }
  }, [selectedAccount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once on mount
  useEffect(() => {
    trackFunnelStep(FunnelFlow.Withdrawal, FunnelStep.Intent);
  }, []);

  const {
    mutateAsync: createNewProfitMethodAsync,
    error: createNewProfitMethodError,
    isPending: createNewProfitMethodIsPending,
  } = useNewProfitMethod();

  const {
    mutateAsync: calculateTransactionsAsync,
    isPending: calculateTransactionsIsPending,
  } = useMutation(calculateSavedPaymentAccountTransactionsMutation());

  useEffect(
    function _showErrorNotification() {
      if (createNewProfitMethodError) {
        notify.error(
          getNewProfitMethodErrorMessage(createNewProfitMethodError, t) ||
            t("withdrawals.errors.unknown") ||
            t("withdrawals.errors.unknown", { lng: "en" })
        );
      }
    },
    [createNewProfitMethodError, t]
  );

  const { currencies } = useCurrencies();

  const formData = useAppSelector((state) => state.form.formData);
  const { getIsValid } = useDynamicForm();

  const serializeProfitAmounts = (
    pa: WithdrawalProfitAmounts
  ): NavigatableWithdrawalProfitAmounts => ({
    ...pa,
    profitAmountLeft: pa.profitAmountLeft.toNumber(),
    profitAmounts: Object.fromEntries(
      Object.entries(pa.profitAmounts).map(([k, v]) => [
        k,
        v?.toNumber() ?? undefined,
      ])
    ),
  });

  const setFile = useCallback(
    async (file: UploadedFile | undefined) => {
      setBankStatementFile(file);

      if (!file) {
        return;
      }

      await uploadBankStatement({
        file,
        onPatch: (fileId, patch) =>
          setBankStatementFile((prev) =>
            prev && prev.id === fileId ? { ...prev, ...patch } : prev
          ),
        onIdGenerated: (fileId, documentId) => {
          setBankStatementFile((prev) =>
            prev && prev.id === fileId
              ? { ...prev, document_id: documentId }
              : prev
          );
        },
        errorMessage: t("withdrawals.bank_statement.error_server"),
      });
    },
    [t]
  );

  const handleNext = async () => {
    if (!selectedAccount || !profitAmounts) {
      return;
    }

    const newProfitPaymentMethod =
      profitAmounts &&
      newProfitMethods?.payment_methods.find(
        ({ id }) => id === profitAmounts.newProfitPaymentMethodId
      );

    // Tracks SPA ids that belong to bank_transfer payment methods, including
    // newly created ones which won't appear in balance.refund_accounts yet.
    const newBankTransferSpaIds = new Set<string>();

    if (newProfitPaymentMethod) {
      const profitAmount =
        profitAmounts.profitAmounts[newProfitPaymentMethod.id] ??
        new Decimal(0);

      const bankStatementMandatory = await isBankStatementMandatory(
        newProfitPaymentMethod.bank_statement_upload_config,
        userData,
        newProfitPaymentMethod.id,
        profitAmount,
        selectedAccount.currency
      );

      const successfulBankStatementId =
        getSuccessfulBankStatementId(bankStatementFile);

      if (bankStatementMandatory && !successfulBankStatementId) {
        notify.error(
          <div className={toastStyles.ToastBody}>
            <span className={toastStyles.ToastTitle}>
              {t("withdrawals.bank_statement.mandatory_toast_title")}
            </span>
            <span className={toastStyles.ToastDescription}>
              {t("withdrawals.bank_statement.mandatory_toast")}
            </span>
          </div>
        );
        return;
      }

      const result = await createNewProfitMethodAsync({
        paymentMethod: newProfitPaymentMethod,
        tradingAccount: selectedAccount,
        amount_with_currency: decimalToAmountCurrencyRequest(
          profitAmount,
          selectedAccount.currency
        ),
        document_id: successfulBankStatementId,
      });

      if (result) {
        // trigger background refetch to update the list of saved payment accounts,
        profitMethodsRefetch();
        // replace the template profit PaymentMethod record with freshly created SavedPaymentAccount
        profitAmounts.profitAmounts = {
          [result.id]: profitAmount,
        };
        profitAmounts.newProfitPaymentMethodId = "";
        setProfitAmounts({ ...profitAmounts });
        if (
          newProfitPaymentMethod.action_type ===
          EnumActionType.EnumActionTypeBankTransfer
        ) {
          newBankTransferSpaIds.add(result.id);
        }
      }
    }

    //TODO improve this, instead of handling one by one we need to clarify if we should have multiple forms being submitted, because the formSlice simply does not support that.
    let newRefundSpaAmounts: AmountsByMethodId = {};
    if (Object.keys(newRefundSavedPaymentMethods).length > 0) {
      const items = Object.entries(newRefundSavedPaymentMethods);
      for (let index = 0; index < items.length; index++) {
        const key = items[index][0];
        const pm_id = key.split("_")[0];
        const currency_code = key.split("_")[1];
        const currency = currencies?.[currency_code];
        const amount = items[index]?.[1];
        // Pre-selected refund PMs (zota, naewe) carry their add-new PM in
        // balance.payout_accounts, not the (BE-narrowed) profit endpoint.
        const method =
          balance?.payout_accounts?.[pm_id]?.payment_method ??
          newProfitMethods?.payment_methods.find(({ id }) => id === pm_id);
        if (method && amount instanceof Decimal && currency) {
          // Abort on creation failure: continuing would submit a refund that
          // silently omits this row. The mutation's onError effect already
          // toasts the error to the user.
          let result: { id?: string } | undefined;
          try {
            result = await createNewProfitMethodAsync({
              paymentMethod: method,
              tradingAccount: selectedAccount,
              amount_with_currency: majorToAmountCurrencyRequest(
                //afaik this value is not used for create method endpoint, so hardcoding it with a zero
                0,
                currency
              ),
            });
          } catch {
            return;
          }
          if (!result?.id) return;
          if (
            method.action_type === EnumActionType.EnumActionTypeBankTransfer
          ) {
            newBankTransferSpaIds.add(result.id);
          }
          const createdKey = `${result.id}_${currency_code}`;
          newRefundSpaAmounts = {
            ...newRefundSpaAmounts,
            [createdKey]: amount,
          };
        }
      }
    }

    /* TODO: passing the data directly to the route might not be how we want to handle this
     *    We should consider using global redux state or local WD context
     */
    // SavedPaymentAccountTransactionsCalculationRequest
    const payload = Object.entries(refundAmounts)
      .concat(Object.entries(newRefundSpaAmounts))
      .reduce<any>((acc, [k, v]) => {
        const originalId = k.split("_")[0];
        const currency = k.split("_")[1];
        if (v === undefined) return acc;
        // Pre-selected refund cards (zota/naewe) submit their own approved
        // refund_account id; add-new rows were already remapped to the
        // freshly created SPA id above. Either way the destination id is the
        // row key's own prefix.
        return [...acc, { id: originalId, originalId, currency, amount: v }];
      }, []);

    const serializedPayload = payload.map((item: any) => ({
      ...item,
      amount:
        item.amount instanceof Decimal ? item.amount.toNumber() : item.amount,
    }));

    const refundCalcRequestsById = new Map<
      string,
      SavedPaymentAccountRefundAmounts
    >();
    for (const item of serializedPayload as Array<{
      id: string;
      originalId: string;
      currency: string;
      amount: number;
    }>) {
      const amount = majorToAmountCurrencyRequest(
        item.amount,
        currencies?.[item.currency]
      );
      const existing = refundCalcRequestsById.get(item.id);
      if (existing) {
        existing.amounts.push(amount);
      } else {
        refundCalcRequestsById.set(item.id, {
          id: item.id,
          selected_currency:
            refundConversionCurrencies[item.originalId] ?? undefined,
          amounts: [amount],
        });
      }
    }
    const refundCalcRequests = Array.from(refundCalcRequestsById.values());

    const profitEntry = Object.entries(profitAmounts.profitAmounts).find(
      ([, v]) => v !== undefined
    );
    const profitCalcRequest = profitEntry
      ? {
          id: profitEntry[0],
          selected_currency: profitConversionState.selected ?? undefined,
          amount_with_currency: decimalToAmountCurrencyRequest(
            profitEntry[1] as Decimal,
            selectedAccount.currency
          ),
        }
      : undefined;

    let calculatedTransactions:
      | CalculateSavedPaymentAccountTransactionsResponse
      | undefined;
    try {
      calculatedTransactions = await calculateTransactionsAsync({
        path: { tradingAccountID: selectedAccount.id },
        body: {
          refund_saved_payment_accounts: refundCalcRequests,
          profit_saved_payment_account: profitCalcRequest,
        },
      });
    } catch (err: any) {
      if (err?.key === EnumErrorKey.WithdrawalInsufficientFunds) {
        setWithdrawalErrors((prev) => ({
          ...prev,
          general: t("withdrawals.validation.insufficient_funds"),
        }));
      } else {
        // Translation keys used dynamically - ensure i18next-parser detects them
        // t('common.bad_request')
        // t('withdrawals.errors.unknown')
        // t('withdrawal.amount_below_minimum_limit')
        notify.error(t(err?.key ?? "withdrawals.errors.unknown"));
      }
      return;
    }

    // Build the comprehensive set of bank transfer payment method ids:
    // both from new-profit-method templates and from payout_accounts (pre-selected add-new methods).
    const bankTransferPmIds = new Set<string>();
    for (const pm of newProfitMethods?.payment_methods ?? []) {
      if (pm.action_type === EnumActionType.EnumActionTypeBankTransfer) {
        bankTransferPmIds.add(pm.id);
      }
    }
    for (const [pmId, entry] of Object.entries(
      balance?.payout_accounts ?? {}
    )) {
      if (
        entry.payment_method?.action_type ===
        EnumActionType.EnumActionTypeBankTransfer
      ) {
        bankTransferPmIds.add(pmId);
      }
    }

    const bankTransferSpaIds: string[] = [
      // Existing bank transfer refund SPAs
      ...(balance?.refund_accounts ?? [])
        .filter((acc) => acc.id && bankTransferPmIds.has(acc.payment_method_id))
        .map((acc) => acc.id as string),
      // Existing bank transfer profit SPAs
      ...(profitMethods ?? [])
        .filter((pm) => bankTransferPmIds.has(pm.payment_method_id))
        .map((pm) => pm.id),
      // Newly created bank transfer SPAs (tracked inline during SPA creation above)
      ...newBankTransferSpaIds,
    ];

    navigate(`../${ERoutes.Summary}`, {
      state: {
        profitAmounts: serializeProfitAmounts(profitAmounts),
        refundAmounts: serializedPayload,
        profitConversionCurrency: profitConversionState.selected ?? undefined,
        refundConversionCurrencies,
        // Seeds Summary's calculation query so it doesn't immediately re-fetch
        // the same result we just validated above.
        calculatedTransactions,
        calculatedTransactionsFetchedAt: Date.now(),
        bankTransferSpaIds,
      },
    });
  };

  const isLoading =
    balanceIsLoading ||
    methodsIsLoading ||
    createNewProfitMethodIsPending ||
    calculateTransactionsIsPending;

  //map that use a combination of the id+currency as key useful to check an account without having to loop twice, since the response has 2 arrays nested, 1st methods, 2nd currencies
  // Pre-selected refund PMs (e.g. zota) add a `${pmId}`-keyed entry for their
  // single add-new card so over-allocation + per-card max account for it.
  const refundAccsMap = addPreselectedAddNewRefundEntries(
    getRefundAccountsByIdAndCurrency(balance),
    balance
  );

  const initRefundAmountLeft =
    moneyToDecimal(balance?.refundable_money) ?? new Decimal(0);

  const [refundAmounts, setRefundAmounts] = useState<AmountsByMethodId>(
    Object.keys(refundAccsMap).reduce(
      (acc: AmountsByMethodId, k: string) => ({ ...acc, [k]: undefined }),
      {}
    )
  );

  const [newRefundSavedPaymentMethods, setNewRefundSavedPaymentMethods] =
    useState<AmountsByMethodId>({});

  const initMaxRefunds = useRef<CurrencyConversionBySpaAndCode | null>(null);

  const { refundAmountLeft, maxRefunds } = useMemo(() => {
    const allRefundAmounts = {
      ...refundAmounts,
      ...newRefundSavedPaymentMethods,
    };
    const refundAmountLeft = calculateRefundAmountLeft(
      refundAccsMap,
      balance,
      initRefundAmountLeft,
      allRefundAmounts
    );
    const ctx: RefundContext = {
      refundAmounts: allRefundAmounts,
      refundMethodsByLedger: refundMethodsByLedger ?? {},
      initMaxRefunds: initMaxRefunds.current ?? {},
      initRefundAmountLeft: initRefundAmountLeft,
      refundAmountLeft: refundAmountLeft,
    };

    const maxRefunds = calcMaxRefunds(refundAccsMap, balance, ctx);

    if (initMaxRefunds.current === null) {
      initMaxRefunds.current = maxRefunds;
    }

    return { refundAmountLeft, maxRefunds };
  }, [
    refundAmounts,
    initRefundAmountLeft,
    newRefundSavedPaymentMethods,
    refundMethodsByLedger,
    balance,
  ]);

  const initialProfitAmount =
    moneyToDecimal(balance?.profit_money) ?? new Decimal(0);

  const [withdrawalErrors, setWithdrawalErrors] = useState<
    Record<string, string>
  >({});

  const [profitConversionState, setProfitConversionState] = useState<{
    required: boolean;
    selected: string | null;
  }>({ required: false, selected: null });

  const [refundConversionCurrencies, setRefundConversionCurrencies] = useState<
    Record<string, string>
  >({});

  const [processingNoticeVisible, setProcessingNoticeVisible] = useState(true);

  const handleRefundConversionChange = useCallback(
    (currencies: Record<string, string>) => {
      setRefundConversionCurrencies(currencies);
    },
    []
  );

  const handleProfitConversionChange = useCallback(
    (required: boolean, selected: string | null) => {
      setProfitConversionState((prev) => {
        if (prev.required === required && prev.selected === selected)
          return prev;
        return { required, selected };
      });
    },
    []
  );

  const isWithdrawalDisabled = useMemo(() => {
    let isNewRefundSpa = false;
    let isNewRefundSpaFormValid = true;

    if (Object.keys(newRefundSavedPaymentMethods).length > 0) {
      const uniquePmIds = [
        ...new Set(
          Object.keys(newRefundSavedPaymentMethods).map((k) => k.split("_")[0])
        ),
      ];
      for (const pmId of uniquePmIds) {
        const method =
          balance?.payout_accounts?.[pmId]?.payment_method ??
          newProfitMethods?.payment_methods.find(({ id }) => id === pmId);
        if (method) {
          isNewRefundSpa = true;
          const methodFormValid = isCardForm(method)
            ? (formData.creditCardForm.formValid ?? false)
            : getIsValid(method.id);
          if (!methodFormValid) {
            isNewRefundSpaFormValid = false;
            break;
          }
        }
      }
    }

    let isNewProfitSpa = false;
    let isNewProfitSpaFormValid = false;
    const newProfitMethodId = profitAmounts?.newProfitPaymentMethodId;
    if (newProfitMethodId) {
      const method = newProfitMethods?.payment_methods.find(
        ({ id }) => id === newProfitMethodId
      );
      if (method) {
        isNewProfitSpa = true;
        isNewProfitSpaFormValid = isCardForm(method)
          ? (formData.creditCardForm.formValid ?? false)
          : getIsValid(method.id);
      }
    }

    const insufficientBalance =
      balance?.profit_money.amount === "0" &&
      balance?.refundable_money.amount === "0";

    const hasSelectedAmounts =
      (profitAmounts &&
        (Object.values(profitAmounts.profitAmounts).some(Boolean) ||
          Object.values(refundAmounts).some(
            (v) => v !== undefined && Boolean(v)
          ))) ||
      Object.keys(newRefundSavedPaymentMethods).length > 0;

    const overAllocated = refundAmountLeft.lessThan(0);

    const hasErrors = Object.values(withdrawalErrors).some(Boolean);

    const profitConversionMissing =
      profitConversionState.required && !profitConversionState.selected;

    return (
      isLoading ||
      hasErrors ||
      insufficientBalance ||
      !hasSelectedAmounts ||
      profitConversionMissing ||
      (isNewRefundSpa && !isNewRefundSpaFormValid) ||
      (isNewProfitSpa && !isNewProfitSpaFormValid) ||
      overAllocated ||
      hasBankStatementInFlight(bankStatementFile) ||
      hasBankStatementError(bankStatementFile)
    );
  }, [
    isLoading,
    withdrawalErrors,
    balance,
    profitAmounts,
    refundAmounts,
    newRefundSavedPaymentMethods,
    newProfitMethods,
    formData,
    getIsValid,
    refundAmountLeft,
    profitConversionState,
    bankStatementFile,
  ]);

  const clearGeneralError = useCallback(() => {
    setWithdrawalErrors((prev) => {
      if (!prev.general) return prev;
      const { general: _general, ...rest } = prev;
      return rest;
    });
  }, []);

  const onRefundChange = useCallback(
    (key: string, amount: Decimal | undefined) => {
      setRefundAmounts((prev) => {
        const next = { ...prev };
        if (amount === undefined) delete next[key];
        else next[key] = amount;
        return next;
      });
      clearGeneralError();
    },
    [clearGeneralError]
  );

  const onNewRefundChange = useCallback(
    (key: string, amount: Decimal | undefined) => {
      setNewRefundSavedPaymentMethods((prev) => {
        const next = { ...prev };
        if (amount === undefined) delete next[key];
        else next[key] = amount;
        return next;
      });
      clearGeneralError();
    },
    [clearGeneralError]
  );

  const setProfitAmountChange = useCallback(
    (change: WithdrawalProfitAmounts) => {
      setProfitAmounts(change);
      clearGeneralError();
    },
    [clearGeneralError]
  );

  if (!selectedAccount) {
    return <Navigate to={`../${ERoutes.Accounts}`} replace />;
  }

  if (error) {
    logger.error("Failed to load withdrawal method info", {
      error: serializeError(error),
    });
    return (
      <ErrorPage
        title={t("withdrawals.methods.errors.failed_load_info")}
        message={t("withdrawals.methods.errors.failed_load_info_text")}
        buttonLabel={t("global.buttons.retry")}
        buttonAction={refetch}
      />
    );
  }

  return (
    <div className="ps-flex ps-flex-col ps-gap-8">
      {exchangeRateUnavailable && (
        <InfoBox
          variant="warning"
          data-testid="withdrawal-exchange-rate-banner"
          title={t("global.exchange_rate_unavailable.title")}
          description={t("global.exchange_rate_unavailable.description")}
        />
      )}
      <AccountBalanceCard
        isLoading={balanceIsLoading}
        account={selectedAccount}
        refundAmount={
          refundAmountLeft.isNegative()
            ? initRefundAmountLeft
            : refundAmountLeft
        }
        profitAmount={
          profitAmounts?.profitAmountLeft ??
          moneyToDecimal(balance?.profit_money) ??
          new Decimal(0)
        }
        data-testid="withdrawal-account-balance-table"
      />
      <div
        className="ps-flex ps-flex-col ps-gap-4"
        data-testid="withdrawal-method-selector"
      >
        <div>
          <h1 className="ps-text-heading-v2-lg ps-flex ps-gap-4 ps-items-center ps-justify-between">
            <span>{t("withdrawals.methods.withdraw")}</span>
            <span>
              <InstructionsModal.Button
                title={t("withdrawals.methods.instructions")}
                data-testid="InstructionModalWithButtonTrigger"
                instructions={WITHDRAW_INSTRUCTIONS}
              >
                {t("withdrawals.methods.instructions_button")}
              </InstructionsModal.Button>
            </span>
          </h1>
          <div className="ps-text-body-v2-sm ps-text-text-base-secondary">
            <Trans
              i18nKey="withdrawals.methods.instructions_paragraph"
              components={{
                link: (
                  <InstructionsModal.Link
                    title={t("withdrawals.methods.instructions")}
                    data-testid="InstructionModalWithLinkTrigger"
                    instructions={WITHDRAW_INSTRUCTIONS}
                  >
                    {t("withdrawals.methods.instructions_link")}
                  </InstructionsModal.Link>
                ),
              }}
            />
          </div>
        </div>
        {processingNoticeVisible && (
          <InfoBox
            data-testid="withdrawal-processing-time-notice"
            title={t("withdrawals.methods.processing_time_notice.title")}
            description={t(
              "withdrawals.methods.processing_time_notice.description"
            )}
            onClose={() => setProcessingNoticeVisible(false)}
          />
        )}
        <div>
          <MethodSelector
            refundAmounts={refundAmounts}
            newRefundAmounts={newRefundSavedPaymentMethods}
            refundAmountLeft={refundAmountLeft}
            maxRefunds={maxRefunds}
            balance={balance}
            tradingAccount={selectedAccount}
            isLoading={!balance || methodsIsLoading}
            profitMethods={profitMethods ?? []}
            newProfitPaymentMethod={newProfitMethods?.payment_methods ?? []}
            initialProfitAmount={initialProfitAmount}
            initialRefundAmount={
              moneyToDecimal(balance?.refundable_money) ?? new Decimal(0)
            }
            onProfitChange={setProfitAmountChange}
            onRefundChange={onRefundChange}
            onNewRefundChange={onNewRefundChange}
            profitError={getNewProfitMethodErrorMessage(
              createNewProfitMethodError,
              t
            )}
            onError={(key, error) =>
              setWithdrawalErrors((prev) => {
                // Prune cleared errors instead of leaving stale keys behind,
                // so a resolved/unmounted card can't keep Next disabled.
                if (error) return { ...prev, [key]: error };
                if (!(key in prev)) return prev;
                const { [key]: _removed, ...rest } = prev;
                return rest;
              })
            }
            onProfitConversionChange={handleProfitConversionChange}
            onRefundConversionChange={handleRefundConversionChange}
            balanceLastUpdated={
              balance != null && balanceDataUpdatedAt > 0
                ? balanceDataUpdatedAt
                : undefined
            }
            balanceNextUpdate={
              balance != null && balanceDataUpdatedAt > 0
                ? balanceDataUpdatedAt + REFUND_BALANCE_REFETCH_INTERVAL_MS
                : undefined
            }
            bankStatementFile={bankStatementFile}
            onBankStatementFileChange={setFile}
          />
        </div>
      </div>
      {refundAmountLeft.lt(0) && (
        <ErrorMessage
          className="ps-justify-end"
          message={t(
            "withdrawals.methods.account_balance_card.refundable_amount_exceeded"
          )}
        />
      )}
      {withdrawalErrors.general && (
        <ErrorMessage
          className="ps-justify-end"
          message={withdrawalErrors.general}
        />
      )}
      <div className="ps-flex ps-justify-end">
        <NavButtons
          className="ps-max-w-[480px]"
          nextLabel={t("global.nav_buttons.continue")}
          backAction={() => navigate(`../${ERoutes.Accounts}`)}
          nextAction={handleNext}
          backDisabled={isLoading}
          nextDisabled={isWithdrawalDisabled}
        />
      </div>
    </div>
  );
};
